import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { TypedCapabilityInputsComponent } from '@app/shared/forms/typed-capability-inputs.component';
import { PlatformBusinessCalendarsService } from '../business-calendars/platform-business-calendars.service';
import {
  calendarIdentity,
  type BusinessCalendar,
} from '../business-calendars/platform-business-calendars.models';
import type {
  WorkflowDefinitionDocument,
  WorkflowTimerAction,
} from './platform-workflow-definition-lifecycle.models';
import { PlatformWorkflowDefinitionLifecycleService } from './platform-workflow-definition-lifecycle.service';
import { WorkflowEditorPendingScope } from './workflow-editor-pending-state';
import { WorkflowOwnerViewState } from './workflow-owner-view-state';
import {
  capabilityIdentity,
  sameCapabilityReference,
} from './workflow-capability-authoring';
import {
  workflowTimerAuthoringSnapshot,
  type WorkflowTimerAuthoringRuntime,
  type WorkflowTimerAuthoringSnapshot,
} from './workflow-timer-catalog.models';
import {
  automationOperation,
  compatibleAutomationContexts,
  nextAutomationId,
  type AutomationField,
  type WorkflowAutomationCatalog,
} from './workflow-automation-authoring';
import {
  timerFingerprint,
  timerOperationEditable,
  timerOperationPolicyError,
  timerMaximumDays,
  timerMaximumMinutes,
} from './workflow-step-timers-authoring';
import {
  applyStepTimerChange,
  isLegacyStepTimerDraft,
  isKnownStepTimer,
  savedStepTimerRows,
  type WorkflowStepTimerV1,
  type WorkflowStepTimerV1Draft,
  type WorkflowStepTimerV2,
  type WorkflowTimerCatalogRequest,
} from './workflow-step-timers-v2-authoring';

type Stage = 'warning' | 'breach';
type TimerScalarField =
  | 'label'
  | 'startCode'
  | 'durationAmount'
  | 'warningLeadMinutes'
  | 'pauseCode'
  | 'resetCode'
  | 'reentryCode'
  | 'dueLocalTime'
  | 'gapCode'
  | 'overlapCode';
type TimerDraft =
  | WorkflowStepTimerV1Draft
  | (Omit<WorkflowStepTimerV2, 'durationAmount'> & {
      durationAmount: number | null;
    });
export interface WorkflowTimerDraftSnapshot {
  timers: unknown[];
  rawBaseline: unknown;
  originalIndex: number | null;
  removalIndex: number | null;
  warningEnabled: boolean;
  overdueEnabled: boolean;
  draft: TimerDraft | null;
  originalId: string | null;
  baseline: string;
  reference: string;
  removalId: string | null;
}
export interface WorkflowTimerOwnedDocumentChange {
  document: WorkflowDefinitionDocument;
  nodeId: string;
  owner: string;
  source: string;
  view: string | null;
  draft: WorkflowTimerDraftSnapshot;
}
interface TimerOrigin {
  generation: number;
  owner: string;
  source: string;
  view: string | null;
  draft: TimerDraft | null;
  stage?: Stage;
  action?: WorkflowTimerAction | null;
  field?: AutomationField;
  input?: WorkflowTimerAction['inputs'][number];
}

@Component({
  selector: 'phms-workflow-step-timers-editor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    UiButtonDirective,
    UiControlDirective,
    TypedCapabilityInputsComponent,
  ],
  templateUrl: './workflow-step-timers-editor.component.html',
  styleUrl: './workflow-step-timers-editor.component.scss',
})
export class WorkflowStepTimersEditorComponent implements OnChanges, OnDestroy {
  private static nextInstanceId = 0;
  private readonly instanceId =
    ++WorkflowStepTimersEditorComponent.nextInstanceId;
  @Input() document: WorkflowDefinitionDocument | null = null;
  @Input() nodeId: string | null = null;
  @Input() disabled = false;
  @Input() canAuthor = true;
  @Input() interactionBlocked = false;
  @Input() resetVersion = 0;
  @Input() workspaceMode = false;
  @Input() optionalStageAuthoring = false;
  @Input() latestDocument: (() => WorkflowDefinitionDocument | null) | null =
    null;
  @Input() ownerContext: (() => unknown) | null = null;
  @Input() viewContext: (() => unknown) | null = null;
  @Input() active: (() => boolean) | null = null;
  @Input() activeView: (() => string | null) | null = null;
  @Input() activeViewState: string | null | undefined = undefined;
  @Output() pendingChangesChange = new EventEmitter<boolean>();
  @Output() documentChange = new EventEmitter<WorkflowDefinitionDocument>();
  @Output() ownedDocumentChange =
    new EventEmitter<WorkflowTimerOwnedDocumentChange>();
  private readonly api = inject(PlatformWorkflowDefinitionLifecycleService);
  private readonly calendarApi = inject(PlatformBusinessCalendarsService);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly ownerViews = inject(WorkflowOwnerViewState, {
    optional: true,
  });
  private context = '';
  private catalogContext = '';
  private generation = 0;
  private catalogRequest: WorkflowTimerCatalogRequest | null = null;
  private catalogLease: {
    request: WorkflowTimerCatalogRequest;
    snapshot: WorkflowTimerAuthoringSnapshot;
  } | null = null;
  private catalogCalendars: BusinessCalendar[] = [];
  private original = '';
  private draftReference = '';
  private dirty = false;
  private originalId: string | null = null;
  private originalIndex: number | null = null;
  private originalTimers: unknown;
  private ownerSnapshot = '';
  private renderOwnerSnapshot = '';
  private namespaceSnapshot = '';
  private savedPins: WorkflowDefinitionDocument['capabilityReferences'] = [];
  private formGeneration = 0;
  private renderedView: string | null = null;
  private focusRequest = 0;
  private destroyed = false;
  private restoring = false;
  private emitting: WorkflowTimerOwnedDocumentChange | null = null;
  private removalId: string | null = null;
  private removalIndex: number | null = null;
  private renderOriginCache: {
    document: WorkflowDefinitionDocument | null;
    origin: TimerOrigin;
  } | null = null;
  timers: unknown[] = [];
  draft: TimerDraft | null = null;
  warningEnabled = true;
  overdueEnabled = true;
  catalog: WorkflowAutomationCatalog | null = null;
  runtime: WorkflowTimerAuthoringRuntime | null = null;
  calendars: BusinessCalendar[] = [];
  loading = false;
  loadError = '';
  calendarError = '';
  error = '';
  readonly stages: Stage[] = ['warning', 'breach'];
  readonly standalone = { standalone: true };
  readonly maximumMinutes = timerMaximumMinutes;
  readonly maximumDays = timerMaximumDays;
  get headingId(): string {
    return `step-timer-heading-${this.instanceId}-${encodeURIComponent(this.nodeId ?? 'unselected')}`;
  }
  get draftSnapshot(): WorkflowTimerDraftSnapshot {
    return structuredClone({
      timers: this.timers,
      rawBaseline: this.originalTimers,
      originalIndex: this.originalIndex,
      removalIndex: this.removalIndex,
      warningEnabled: this.warningEnabled,
      overdueEnabled: this.overdueEnabled,
      draft: this.draft,
      originalId: this.originalId,
      baseline: this.original,
      reference: this.draftReference,
      removalId: this.removalId,
    });
  }
  get editorView(): { origin: TimerOrigin } {
    return { origin: this.origin() };
  }
  stageOrigin(stage: Stage, field?: AutomationField): TimerOrigin {
    return {
      ...this.origin(),
      stage,
      action: this.stageAction(stage),
      field,
      input: field ? this.binding(stage, field) : undefined,
    };
  }
  stageView(stage: Stage, field?: AutomationField): { origin: TimerOrigin } {
    return { origin: this.stageOrigin(stage, field) };
  }
  private origin(): TimerOrigin {
    const owner = this.ownerKey(),
      view = this.activeView?.() ?? null,
      cached = this.renderOriginCache;
    if (
      cached?.document === this.document &&
      cached.origin.generation === this.formGeneration &&
      cached.origin.owner === owner &&
      cached.origin.view === view &&
      cached.origin.draft === this.draft
    )
      return cached.origin;
    const origin: TimerOrigin = {
      generation: this.formGeneration,
      owner,
      source: JSON.stringify(this.document),
      view,
      draft: this.draft,
    };
    this.renderOriginCache = { document: this.document, origin };
    return origin;
  }
  private currentDocument(): WorkflowDefinitionDocument | null {
    return this.latestDocument ? this.latestDocument() : this.document;
  }
  private ownerKey(): string {
    return JSON.stringify(this.ownerContext?.() ?? null);
  }
  private namespace(document = this.currentDocument()): string {
    return JSON.stringify([
      this.documentReference(document),
      this.viewContext?.() ?? this.ownerContext?.() ?? null,
    ]);
  }
  private ownerCurrent(): boolean {
    return (
      !this.destroyed &&
      (!this.workspaceMode ||
        (this.ownerSnapshot === this.ownerKey() &&
          this.namespaceSnapshot === this.namespace()))
    );
  }
  private scopeFingerprint(document: WorkflowDefinitionDocument): string {
    if (!this.workspaceMode) return timerFingerprint(document, this.nodeId);
    const matches = Array.isArray(document.nodes)
      ? document.nodes.filter(
          (node) => node?.id === this.nodeId && node.typeCode === 'human_step',
        )
      : [];
    const behavior =
      matches.length === 1 ? matches[0].declaration?.behavior : undefined;
    return JSON.stringify([
      matches.length,
      behavior && Object.prototype.hasOwnProperty.call(behavior, 'timers'),
      behavior?.timers,
    ]);
  }
  private pinsCurrent(document: WorkflowDefinitionDocument): boolean {
    if (!this.workspaceMode) return true;
    const current = document.capabilityReferences ?? [];
    return (
      Array.isArray(current) &&
      (this.savedPins ?? []).every((pin) => {
        if (!pin?.key) return false;
        const matches = current.filter(
          (item) =>
            item?.key &&
            capabilityIdentity(item.key) === capabilityIdentity(pin.key),
        );
        return matches.length === 1 && sameCapabilityReference(matches[0], pin);
      })
    );
  }
  private bufferCurrent(document: WorkflowDefinitionDocument | null): boolean {
    return (
      !!document &&
      this.ownerCurrent() &&
      this.documentReference(document) === this.context &&
      (!this.draftReference ||
        this.documentReference(document) === this.draftReference) &&
      (!this.original || this.scopeFingerprint(document) === this.original) &&
      this.pinsCurrent(document)
    );
  }
  private renderPresentationCurrent(): boolean {
    const host = this.element.nativeElement;
    return (
      !this.destroyed &&
      this.renderOwnerSnapshot === this.ownerKey() &&
      this.documentReference(this.currentDocument()) === this.context &&
      JSON.stringify(this.document) ===
        JSON.stringify(this.currentDocument()) &&
      (!this.active || this.active()) &&
      !host.closest('[hidden],[inert],[aria-hidden="true"]') &&
      (!this.activeView ||
        (this.activeView() !== null &&
          this.activeView() === this.renderedView &&
          (this.activeViewState === undefined ||
            this.activeViewState === this.renderedView)))
    );
  }
  private presentationCurrent(): boolean {
    return this.ownerCurrent() && this.renderPresentationCurrent();
  }
  private renderCurrent(origin?: TimerOrigin): boolean {
    return (
      !this.workspaceMode ||
      (this.renderPresentationCurrent() &&
        !this.ownerViews?.focusSuspended() &&
        JSON.stringify(this.document) ===
          JSON.stringify(this.currentDocument()) &&
        (!origin ||
          (origin.generation === this.formGeneration &&
            origin.owner === this.ownerKey() &&
            origin.source === JSON.stringify(this.currentDocument()) &&
            origin.view === (this.activeView?.() ?? null) &&
            origin.draft === this.draft)))
    );
  }
  private nativeCurrent(origin?: TimerOrigin): boolean {
    if (this.restoring) return false;
    if (!this.workspaceMode) return true;
    return (
      this.presentationCurrent() &&
      !this.ownerViews?.focusSuspended() &&
      this.bufferCurrent(this.currentDocument()) &&
      JSON.stringify(this.document) ===
        JSON.stringify(this.currentDocument()) &&
      (!origin ||
        (origin.generation === this.formGeneration &&
          origin.owner === this.ownerKey() &&
          origin.source === JSON.stringify(this.currentDocument()) &&
          origin.view === (this.activeView?.() ?? null) &&
          origin.draft === this.draft &&
          (origin.stage === undefined ||
            origin.action === this.stageAction(origin.stage)) &&
          (!origin.field ||
            (!!origin.stage &&
              this.operation(origin.stage)?.input.fields.includes(
                origin.field,
              ) === true &&
              origin.input === this.binding(origin.stage, origin.field)))))
    );
  }
  private restore(model: NgModel | undefined, value: unknown): void {
    if (!model || this.restoring) return;
    this.restoring = true;
    try {
      model.control.setValue(value, { emitEvent: false });
    } finally {
      this.restoring = false;
    }
  }
  timerValueChanged(
    key: TimerScalarField,
    value: unknown,
    origin: TimerOrigin,
    model?: NgModel,
  ): void {
    if (
      !this.nativeCurrent(origin) ||
      this.editingDisabled ||
      !this.draft ||
      (key === 'warningLeadMinutes' && !this.stageEnabled('warning')) ||
      (key === 'durationAmount' || key === 'warningLeadMinutes'
        ? value !== null && typeof value !== 'number'
        : typeof value !== 'string' &&
          !(
            value === null &&
            ['dueLocalTime', 'gapCode', 'overlapCode'].includes(key)
          ))
    ) {
      this.restore(model, this.draft?.[key]);
      return;
    }
    Object.assign(this.draft, { [key]: value });
    this.changed(origin);
  }
  failureChanged(
    stage: Stage,
    value: unknown,
    origin: TimerOrigin,
    model?: NgModel,
  ): void {
    const action = this.stageAction(stage);
    if (
      !this.nativeCurrent(origin) ||
      this.stageDisabled(stage) ||
      !action ||
      typeof value !== 'string'
    ) {
      this.restore(model, action?.failurePolicy);
      return;
    }
    action.failurePolicy = value;
    this.changed(origin);
  }
  refreshAvailability(origin?: TimerOrigin): void {
    if (this.nativeCurrent(origin)) void this.loadCatalog();
  }

  get node() {
    const nodes =
      (Array.isArray(this.document?.nodes) ? this.document.nodes : []).filter(
        (node) => node?.id === this.nodeId && node.typeCode === 'human_step',
      ) ?? [];
    return nodes.length === 1 ? nodes[0] : null;
  }
  get editingDisabled(): boolean {
    return (
      this.disabled ||
      !this.canAuthor ||
      this.interactionBlocked ||
      this.loading ||
      !this.node ||
      !!this.savedShapeIssue ||
      !this.catalog ||
      (this.draft?.version === 2 && !this.optionalReady)
    );
  }
  private requestFor(
    document: WorkflowDefinitionDocument,
    generation = this.generation,
  ): WorkflowTimerCatalogRequest {
    return {
      workflowCode: document.workflowCode,
      packReference: structuredClone(document.packReference),
      sourceContext: JSON.stringify(document),
      owner: this.ownerKey(),
      namespace: this.namespace(document),
      generation,
    };
  }
  private leaseCurrent(document = this.currentDocument()): boolean {
    return (
      !!document &&
      !!this.catalogLease &&
      !this.loading &&
      !this.destroyed &&
      JSON.stringify(this.catalogLease.request) ===
        JSON.stringify(this.requestFor(document))
    );
  }
  get optionalReady(): boolean {
    const runtime = this.catalogLease?.snapshot.runtime;
    return (
      this.optionalStageAuthoring &&
      this.workspaceMode &&
      !!runtime?.producerEnabled &&
      !!runtime.optionalStagesEnabled &&
      !!runtime.optionalFormatExecutable &&
      this.leaseCurrent()
    );
  }
  get optionalUnavailableReason(): string {
    if (!this.optionalStageAuthoring || !this.workspaceMode) return '';
    if (this.loading)
      return 'Refreshing timer availability. Your input is kept; retry Apply or Save after it finishes.';
    const runtime = this.catalogLease?.snapshot.runtime;
    if (
      runtime &&
      (!runtime.producerEnabled ||
        !runtime.optionalStagesEnabled ||
        !runtime.optionalFormatExecutable)
    )
      return 'Last check: optional timer stages were unavailable. Refresh availability for this draft and author. Timer1 authoring is unchanged.';
    return this.leaseCurrent()
      ? ''
      : 'Refresh availability for this exact draft and author before changing optional timer stages. Your input is kept.';
  }
  stageEnabled(stage: Stage): boolean {
    return (
      this.draft?.version !== 2 ||
      (stage === 'warning' ? this.warningEnabled : this.overdueEnabled)
    );
  }
  setStageEnabled(
    stage: Stage,
    value: string,
    origin: TimerOrigin,
    model?: NgModel,
  ): void {
    if (
      !this.nativeCurrent(origin) ||
      this.editingDisabled ||
      this.draft?.version !== 2 ||
      !['on', 'off'].includes(value)
    ) {
      this.restore(model, this.stageEnabled(stage) ? 'on' : 'off');
      return;
    }
    if (stage === 'warning') this.warningEnabled = value === 'on';
    else this.overdueEnabled = value === 'on';
    this.formGeneration++;
    this.changed();
  }
  get ambiguous(): boolean {
    return (
      new Set(this.timers.map((timer) => this.timerId(timer))).size !==
      this.timers.length
    );
  }
  get savedShapeIssue(): string {
    const declaration = this.node?.declaration,
      behavior = declaration?.behavior,
      raw = behavior?.timers;
    return (declaration != null &&
      (typeof declaration !== 'object' || Array.isArray(declaration))) ||
      (behavior != null &&
        (typeof behavior !== 'object' || Array.isArray(behavior))) ||
      (raw != null && !Array.isArray(raw)) ||
      (Array.isArray(raw) &&
        raw.some(
          (timer) =>
            !timer || typeof timer !== 'object' || Array.isArray(timer),
        )) ||
      (this.document?.capabilityReferences != null &&
        !Array.isArray(this.document.capabilityReferences))
      ? 'The saved timer configuration is unavailable for editing. Its original content is preserved.'
      : '';
  }
  get savedConfiguration(): unknown {
    return this.node?.declaration?.behavior?.timers;
  }
  private timerId(timer: unknown): string | null {
    return savedStepTimerRows([timer])?.[0].id ?? null;
  }
  private loadSavedTimers(): void {
    this.originalTimers = structuredClone(this.savedConfiguration);
    this.timers = Array.isArray(this.originalTimers)
      ? structuredClone(this.originalTimers)
      : [];
  }
  timerLabel(timer: unknown): string {
    return timer &&
      typeof timer === 'object' &&
      !Array.isArray(timer) &&
      'label' in timer &&
      typeof timer.label === 'string'
      ? timer.label
      : 'Saved timer';
  }
  get operations() {
    return this.catalog?.operations.filter(timerOperationEditable) ?? [];
  }
  get dayBased(): boolean {
    return (
      this.draft?.durationUnit === 'calendar_days' ||
      this.draft?.durationUnit === 'working_days'
    );
  }
  get calendarSelection(): string {
    return this.draft?.calendar ? calendarIdentity(this.draft.calendar) : '';
  }
  get retainedCalendar(): boolean {
    return (
      !!this.draft?.calendar &&
      !this.calendars.some(
        (calendar) =>
          calendarIdentity(calendar) === this.calendarSelection &&
          calendar.contentHash === this.draft!.calendar!.contentHash,
      )
    );
  }

  ngOnChanges(changes: SimpleChanges = {}): void {
    const view = this.activeView?.() ?? null;
    if (
      this.workspaceMode &&
      (this.activeViewState === undefined || this.activeViewState === view) &&
      this.renderedView !== view
    ) {
      this.renderedView = view;
      this.formGeneration++;
      this.focusRequest++;
    }
    const context = this.workspaceMode
      ? this.documentReference(this.document)
      : JSON.stringify([
          this.document?.workflowCode,
          this.nodeId,
          this.resetVersion,
        ]);
    if (
      this.workspaceMode &&
      (this.activeViewState === undefined || this.activeViewState === view) &&
      JSON.stringify(this.document) ===
        JSON.stringify(this.currentDocument()) &&
      (changes['document'] ||
        changes['activeViewState'] ||
        changes['workspaceMode'] ||
        changes['ownerContext'])
    )
      this.renderOwnerSnapshot = this.ownerKey();
    if (
      this.workspaceMode &&
      this.dirty &&
      changes['document'] &&
      this.namespaceSnapshot === this.namespace(this.document) &&
      context === this.context &&
      !!this.document &&
      this.scopeFingerprint(this.document) === this.original &&
      this.pinsCurrent(this.document)
    )
      this.ownerSnapshot = this.ownerKey();
    if (this.workspaceMode && changes['document']) {
      this.formGeneration++;
      this.focusRequest++;
    }
    if (context !== this.context) {
      if (!this.workspaceMode || !this.dirty || changes['resetVersion']) {
        this.context = context;
        this.resetPendingChanges();
      }
    } else if (!this.dirty && !this.draft) this.loadSavedTimers();
    if (
      this.workspaceMode &&
      !this.dirty &&
      JSON.stringify(this.document) ===
        JSON.stringify(this.currentDocument()) &&
      (changes['document'] ||
        changes['workspaceMode'] ||
        (changes['activeViewState'] && this.activeViewState === view))
    ) {
      // A clean inspection has no pending input to preserve. Refresh its actual
      // saved row before adopting the new baseline, retaining only a unique ID.
      if (
        changes['document'] &&
        this.document &&
        this.scopeFingerprint(this.document) !== this.original
      ) {
        const selectedId = this.originalId;
        this.loadSavedTimers();
        const matches =
          selectedId === null
            ? []
            : this.timers.filter((timer) => this.timerId(timer) === selectedId);
        this.draft =
          matches.length === 1 && this.canInspectEdit(matches[0])
            ? structuredClone(matches[0])
            : null;
        this.readStageSelection();
        this.originalId = this.draft ? selectedId : null;
        this.originalIndex = this.draft
          ? this.timers.indexOf(matches[0])
          : null;
        this.formGeneration++;
        this.focusRequest++;
      }
      this.ownerSnapshot = this.ownerKey();
      this.namespaceSnapshot = this.namespace(this.document);
      this.original = this.document ? this.scopeFingerprint(this.document) : '';
      this.draftReference = this.documentReference(this.document);
      this.savedPins = Array.isArray(this.document?.capabilityReferences)
        ? structuredClone(this.document.capabilityReferences)
        : [];
    }
    const catalogContext = JSON.stringify([
      this.document?.workflowCode,
      this.document?.packReference,
      this.workspaceMode ? this.ownerKey() : null,
      this.optionalStageAuthoring && this.workspaceMode
        ? JSON.stringify(this.document)
        : null,
    ]);
    if (
      this.document &&
      catalogContext !== this.catalogContext &&
      (!this.optionalStageAuthoring ||
        !this.workspaceMode ||
        this.renderPresentationCurrent())
    ) {
      this.catalogContext = catalogContext;
      void this.loadCatalog();
    }
  }
  ngOnDestroy(): void {
    this.destroyed = true;
    this.generation++;
    this.formGeneration++;
    this.focusRequest++;
  }
  async loadCatalog(): Promise<void> {
    const document = this.currentDocument();
    if (
      !document ||
      (this.workspaceMode &&
        (!this.ownerCurrent() || !this.bufferCurrent(document)))
    )
      return;
    const generation = ++this.generation,
      request = this.requestFor(document, generation);
    this.catalogRequest = request;
    this.catalogLease = null;
    this.loading = true;
    this.loadError = '';
    this.calendarError = '';
    const results = await Promise.allSettled([
      firstValueFrom(this.api.getTimerAuthoringCatalog(document)),
      firstValueFrom(this.calendarApi.getCatalog()),
    ]);
    if (generation !== this.generation || this.destroyed) return;
    this.loading = false;
    if (
      this.workspaceMode &&
      (!this.currentDocument() ||
        !this.ownerCurrent() ||
        JSON.stringify(request) !==
          JSON.stringify(this.requestFor(this.currentDocument()!, generation)))
    ) {
      this.loadError =
        'Timer availability changed before the response arrived. Your input is kept; refresh availability and retry.';
      return;
    }
    if (this.workspaceMode) {
      this.formGeneration++;
      this.focusRequest++;
    }
    const [operations, calendars] = results;
    this.catalog = null;
    this.runtime = null;
    if (
      operations.status === 'fulfilled' &&
      workflowTimerAuthoringSnapshot(operations.value)
    ) {
      const snapshot = structuredClone(operations.value);
      this.catalogLease = { request, snapshot };
      this.catalog = structuredClone(snapshot.catalog);
      this.runtime = { ...snapshot.runtime };
    }
    if (!this.catalog)
      this.loadError =
        'Installed timer operations could not be verified. Entered and saved settings are kept; refresh availability before applying.';
    if (
      calendars.status === 'fulfilled' &&
      calendars.value.contractVersion === 1
    ) {
      this.catalogCalendars = structuredClone(calendars.value.calendars);
      this.calendars = structuredClone(this.catalogCalendars);
    } else {
      this.catalogCalendars = [];
      this.calendars = [];
      this.calendarError =
        'Published calendar versions could not be loaded. Elapsed-minute timers remain available.';
    }
  }
  beginPrimaryAdd(origin?: TimerOrigin): void {
    this.addTimer(this.optionalReady, origin);
  }
  beginAdd(origin?: TimerOrigin): void {
    this.addTimer(false, origin);
  }
  private addTimer(optional: boolean, origin?: TimerOrigin): void {
    if (
      !this.nativeCurrent(origin) ||
      this.editingDisabled ||
      this.ambiguous ||
      this.timers.length >= 16 ||
      !this.applyPendingChanges()
    )
      return;
    if (optional && !this.optionalReady) {
      void this.loadCatalog();
      this.error =
        'Refreshing timer availability after applying pending input. Retry Add step timer when it finishes.';
      return;
    }
    const used = this.document!.nodes.flatMap((node) =>
      (Array.isArray(node?.declaration?.behavior?.timers)
        ? node.declaration!.behavior.timers
        : []
      )
        .map((timer) => this.timerId(timer))
        .filter((id): id is string => id !== null),
    );
    this.original = this.scopeFingerprint(this.document!);
    this.draftReference = this.documentReference(this.document);
    this.savedPins = structuredClone(this.document?.capabilityReferences ?? []);
    this.originalId = null;
    this.originalIndex = null;
    this.originalTimers = structuredClone(this.savedConfiguration);
    this.draft = {
      version: 1,
      id: nextAutomationId('timer', used),
      label: '',
      startCode: '',
      durationUnit: '',
      durationAmount: null,
      calendar: null,
      dueLocalTime: null,
      gapCode: null,
      overlapCode: null,
      pauseCode: '',
      resetCode: '',
      reentryCode: '',
      warningLeadMinutes: null,
      warningAction: null,
      breachAction: null,
    };
    if (optional) {
      this.draft = {
        version: 2,
        id: this.draft.id,
        label: '',
        startCode: 'on_entry',
        durationUnit: 'elapsed_minutes',
        durationAmount: null,
        calendar: null,
        dueLocalTime: null,
        gapCode: null,
        overlapCode: null,
        pauseCode: 'disabled',
        resetCode: 'disabled',
        reentryCode: 'restart_new_occurrence',
      };
    }
    this.warningEnabled = !optional;
    this.overdueEnabled = !optional;
    this.changed();
    this.focusPendingChanges(this.workspaceMode);
  }
  canInspectEdit(timer: unknown): timer is TimerDraft {
    return (
      isLegacyStepTimerDraft(timer) ||
      (this.optionalStageAuthoring &&
        this.workspaceMode &&
        isKnownStepTimer(timer) &&
        timer.version === 2)
    );
  }
  select(timer: unknown, origin?: TimerOrigin): void {
    const index = this.timers.indexOf(timer),
      id = this.timerId(timer);
    if (
      !this.nativeCurrent(origin) ||
      index < 0 ||
      this.disabled ||
      this.interactionBlocked ||
      this.loading ||
      !this.node ||
      this.ambiguous ||
      !this.canInspectEdit(timer) ||
      !this.applyPendingChanges()
    )
      return;
    const current = this.timers[index];
    if (
      !this.canInspectEdit(current) ||
      current.id !== id ||
      this.timers.filter((item) => this.timerId(item) === id).length !== 1
    )
      return;
    this.original = this.scopeFingerprint(this.document!);
    this.draftReference = this.documentReference(this.document);
    this.savedPins = structuredClone(this.document?.capabilityReferences ?? []);
    this.originalId = current.id;
    this.originalIndex = index;
    this.originalTimers = structuredClone(this.savedConfiguration);
    this.draft = structuredClone(current);
    this.readStageSelection();
    this.error = '';
    this.formGeneration++;
    this.focusPendingChanges(this.workspaceMode);
  }
  changed(origin?: TimerOrigin): void {
    if (origin && !this.nativeCurrent(origin)) return;
    this.dirty = true;
    this.error = '';
    this.pendingChangesChange.emit(true);
  }
  changeDuration(unit: string, origin?: TimerOrigin, model?: NgModel): void {
    if (!this.nativeCurrent(origin) || this.editingDisabled || !this.draft) {
      this.restore(model, this.draft?.durationUnit);
      return;
    }
    this.draft.durationUnit = unit;
    this.formGeneration++;
    if (unit === 'elapsed_minutes') {
      this.draft.calendar = null;
      this.draft.dueLocalTime = null;
      this.draft.gapCode = null;
      this.draft.overlapCode = null;
    }
    this.changed();
  }
  chooseCalendar(
    identity: string,
    origin?: TimerOrigin,
    model?: NgModel,
  ): void {
    if (!this.nativeCurrent(origin) || this.editingDisabled || !this.draft) {
      this.restore(model, this.calendarSelection);
      return;
    }
    const matches = this.calendars.filter(
      (item) => calendarIdentity(item) === identity,
    );
    if (matches.length > 1 || (identity && matches.length !== 1)) {
      this.restore(model, this.calendarSelection);
      return;
    }
    const calendar = matches[0];
    this.draft.calendar = calendar
      ? {
          code: calendar.code,
          versionCode: calendar.versionCode,
          contentHash: calendar.contentHash,
        }
      : null;
    this.changed();
  }
  calendarIdentity = calendarIdentity;
  private readStageSelection(): void {
    this.warningEnabled =
      this.draft?.version !== 2 ||
      this.draft.warningAction != null ||
      this.draft.warningLeadMinutes != null;
    this.overdueEnabled =
      this.draft?.version !== 2 || this.draft.breachAction != null;
  }
  stageAction(stage: Stage): WorkflowTimerAction | null {
    if (!this.stageEnabled(stage)) return null;
    return (
      this.draft?.[stage === 'warning' ? 'warningAction' : 'breachAction'] ??
      null
    );
  }
  operation(stage: Stage) {
    return automationOperation(this.catalog, this.stageAction(stage));
  }
  operationAvailable(stage: Stage): boolean {
    const operation = this.operation(stage);
    return (
      !!operation &&
      timerOperationEditable(operation) &&
      !this.operationPolicyError(operation)
    );
  }
  operationPolicyError(
    operation: Parameters<typeof timerOperationPolicyError>[0],
  ): string | null {
    return timerOperationPolicyError(operation, this.document, this.nodeId);
  }
  stageUnavailableReason(stage: Stage): string {
    const operation = this.operation(stage);
    return (
      (operation && this.operationPolicyError(operation)) ||
      'The exact saved operation is unavailable. It is kept unchanged; choose a new operation explicitly to repair it.'
    );
  }
  stageDisabled(stage: Stage): boolean {
    const operation = this.operation(stage);
    return (
      this.editingDisabled || !operation || !this.operationAvailable(stage)
    );
  }
  get exampleAvailable(): boolean {
    return this.operations.some(
      (operation) => operation.command.key.code === 'reference.calculate',
    );
  }
  get adding(): boolean {
    return this.originalId === null;
  }
  operationId(stage: Stage): string {
    const action = this.stageAction(stage);
    return action ? capabilityIdentity(action.command.key) : '';
  }
  selectOperation(
    stage: Stage,
    identity: string,
    origin?: TimerOrigin,
    model?: NgModel,
  ): void {
    if (
      !this.nativeCurrent(origin) ||
      this.editingDisabled ||
      !this.draft ||
      !this.stageEnabled(stage)
    ) {
      this.restore(model, this.operationId(stage));
      return;
    }
    const matches = this.operations.filter(
      (item) => capabilityIdentity(item.command.key) === identity,
    );
    if (matches.length > 1 || (identity && matches.length !== 1)) {
      this.restore(model, this.operationId(stage));
      return;
    }
    const operation = matches[0];
    if (operation && this.operationPolicyError(operation)) {
      this.error = this.operationPolicyError(operation)!;
      this.restore(model, this.operationId(stage));
      return;
    }
    this.draft[stage === 'warning' ? 'warningAction' : 'breachAction'] =
      operation
        ? {
            id: `${this.draft.id}-${stage}`,
            typeCode: 'command',
            command: structuredClone(operation.command),
            inputs: [],
            outcomes: [...operation.outcomes],
            group: `${this.draft.id}-${stage}`,
            order: 0,
            mode: 'durable',
            failurePolicy: '',
            caller: 'scoped-system',
            requiredForProgression: false,
          }
        : null;
    this.formGeneration++;
    this.changed();
  }
  binding(stage: Stage, field: AutomationField) {
    return this.stageAction(stage)?.inputs.find(
      (input) => input.field === field.name,
    );
  }
  contextChoices(field: AutomationField) {
    return this.catalog && this.document
      ? compatibleAutomationContexts(
          this.catalog,
          'scheduled-item',
          field,
          this.document,
        ).filter((item) =>
          [
            'workflow.id',
            'occurrence.id',
            'subject.id',
            'workflow.etag',
            'initiatingActor.id',
          ].includes(item.code),
        )
      : [];
  }
  setSource(
    stage: Stage,
    field: AutomationField,
    source: string,
    origin?: TimerOrigin,
    model?: NgModel,
  ): void {
    const action = this.stageAction(stage);
    if (
      !this.nativeCurrent(origin) ||
      this.stageDisabled(stage) ||
      !action ||
      !['', 'context', 'literal'].includes(source)
    ) {
      this.restore(model, this.binding(stage, field)?.source ?? '');
      return;
    }
    action.inputs = action.inputs.filter((input) => input.field !== field.name);
    if (source === 'context')
      action.inputs.push({ field: field.name, source, context: '' });
    else if (source === 'literal')
      action.inputs.push(
        ['number', 'integer'].includes(field.type)
          ? { field: field.name, source, literalJson: '' }
          : {
              field: field.name,
              source,
              value: field.type === 'string' ? '' : undefined,
            },
      );
    this.changed();
  }
  setContext(
    stage: Stage,
    field: AutomationField,
    context: string,
    origin?: TimerOrigin,
    model?: NgModel,
  ): void {
    const input = this.binding(stage, field);
    if (
      !this.nativeCurrent(origin) ||
      this.stageDisabled(stage) ||
      !input ||
      input.source !== 'context' ||
      typeof context !== 'string'
    ) {
      this.restore(model, input?.context ?? '');
      return;
    }
    input.context = context;
    this.changed();
  }
  exactLiteral(stage: Stage, field: AutomationField): string {
    const input = this.binding(stage, field);
    return (
      input?.literalJson ?? (input?.value == null ? '' : String(input.value))
    );
  }
  setExactLiteral(
    stage: Stage,
    field: AutomationField,
    value: string,
    origin?: TimerOrigin,
    model?: NgModel,
  ): void {
    const input = this.binding(stage, field);
    if (
      !this.nativeCurrent(origin) ||
      this.stageDisabled(stage) ||
      !input ||
      input.source !== 'literal' ||
      typeof value !== 'string'
    ) {
      this.restore(model, this.exactLiteral(stage, field));
      return;
    }
    delete input.value;
    input.literalJson = value;
    this.changed();
  }
  literalValues(stage: Stage, field: AutomationField): Record<string, unknown> {
    return { [field.name]: this.binding(stage, field)?.value };
  }
  setLiteralValues(
    stage: Stage,
    field: AutomationField,
    values: Record<string, unknown>,
    origin?: TimerOrigin,
  ): void {
    const input = this.binding(stage, field);
    if (
      !this.nativeCurrent(origin) ||
      this.stageDisabled(stage) ||
      !input ||
      input.source !== 'literal'
    )
      return;
    delete input.literalJson;
    input.value = values[field.name];
    this.changed();
  }
  setLiteralValue(
    stage: Stage,
    field: AutomationField,
    value: unknown,
    origin: TimerOrigin,
    model?: NgModel,
  ): void {
    const input = this.binding(stage, field);
    if (
      !this.nativeCurrent(origin) ||
      this.stageDisabled(stage) ||
      !input ||
      input.source !== 'literal'
    ) {
      this.restore(model, input?.value);
      return;
    }
    delete input.literalJson;
    input.value = value;
    this.changed(origin);
  }
  fillExample(origin?: TimerOrigin): void {
    if (
      !this.nativeCurrent(origin) ||
      this.editingDisabled ||
      !this.draft ||
      this.originalId
    )
      return;
    const matches = this.operations.filter(
      (item) => item.command.key.code === 'reference.calculate',
    );
    const operation = matches.length === 1 ? matches[0] : undefined;
    if (!operation) {
      this.error =
        'The real reference numeric operation is not installed for this workflow.';
      return;
    }
    Object.assign(this.draft, {
      label: 'Synthetic deadline example',
      startCode: 'on_entry',
      durationUnit: 'elapsed_minutes',
      durationAmount: 60,
      calendar: null,
      dueLocalTime: null,
      gapCode: null,
      overlapCode: null,
      pauseCode: 'manual_before_warning',
      resetCode: 'restart_from_now',
      reentryCode: 'restart_new_occurrence',
      warningLeadMinutes: 15,
    });
    this.warningEnabled = true;
    this.overdueEnabled = true;
    for (const stage of this.stages) {
      this.selectOperation(stage, capabilityIdentity(operation.command.key));
      const action = this.stageAction(stage)!;
      action.failurePolicy = 'stop';
      action.inputs = [
        ['left', '2'],
        ['right', '3'],
        ['threshold', '5'],
      ].map(([field, literalJson]) => ({
        field,
        source: 'literal',
        literalJson,
      }));
    }
    this.changed();
  }
  hasPendingChanges(): boolean {
    return this.dirty;
  }
  private documentReference(
    document: WorkflowDefinitionDocument | null,
  ): string {
    return JSON.stringify([
      document?.workflowCode,
      document?.definitionVersionCode,
      document?.packReference,
      this.nodeId,
      this.resetVersion,
    ]);
  }
  pendingEditorScopes(
    latestDocument: () => WorkflowDefinitionDocument | null = () =>
      this.currentDocument(),
  ): readonly WorkflowEditorPendingScope[] {
    return [
      {
        id: 'timers',
        label: 'step timer input',
        state: () => {
          if (!this.dirty) return 'clean';
          const latest = latestDocument();
          if (
            !latest ||
            !this.ownerCurrent() ||
            !this.pinsCurrent(latest) ||
            this.documentReference(latest) !== this.draftReference ||
            this.scopeFingerprint(latest) !== this.original
          )
            return 'stale';
          if (
            this.editingDisabled ||
            !this.draft ||
            !this.nodeId ||
            this.ambiguous
          )
            return 'invalid';
          try {
            this.candidateDocument(latest);
            return 'valid';
          } catch {
            return 'invalid';
          }
        },
        fingerprint: () => {
          const latest = latestDocument();
          return JSON.stringify([
            this.dirty,
            this.original,
            this.originalId,
            this.draftReference,
            this.documentReference(latest),
            latest && this.scopeFingerprint(latest),
            this.ownerSnapshot,
            this.ownerKey(),
            this.namespaceSnapshot,
            this.namespace(),
            this.savedPins,
            this.draft,
            this.warningEnabled,
            this.overdueEnabled,
            this.timers,
            this.catalog,
            this.calendars,
            this.loadError,
            this.calendarError,
            this.disabled,
            this.canAuthor,
            this.interactionBlocked,
            this.loading,
          ]);
        },
        apply: () => this.applyPendingChanges(latestDocument()),
        discard: () => {
          this.document = latestDocument();
          this.context = this.workspaceMode
            ? this.documentReference(this.document)
            : this.context;
          this.resetPendingChanges();
        },
        focus: () => this.focusPendingChanges(this.workspaceMode),
      },
    ];
  }
  private legacyCandidate(): WorkflowStepTimerV1 {
    const draft = this.draft;
    if (
      !draft ||
      draft.version !== 1 ||
      typeof draft.label !== 'string' ||
      typeof draft.durationAmount !== 'number' ||
      typeof draft.warningLeadMinutes !== 'number' ||
      !draft.warningAction ||
      !draft.breachAction
    )
      throw new Error('Review the selected timer input.');
    return {
      ...draft,
      version: 1,
      label: draft.label.trim(),
      durationAmount: draft.durationAmount,
      warningLeadMinutes: draft.warningLeadMinutes,
      warningAction: structuredClone(draft.warningAction),
      breachAction: structuredClone(draft.breachAction),
    };
  }
  private optionalCandidate(): WorkflowStepTimerV2 {
    const draft = this.draft;
    if (
      !draft ||
      draft.version !== 2 ||
      typeof draft.durationAmount !== 'number'
    )
      throw new Error('Enter the deadline duration.');
    const { warningAction, warningLeadMinutes, breachAction, ...deadline } =
      draft;
    const candidate: WorkflowStepTimerV2 = {
      ...deadline,
      version: 2,
      label: draft.label.trim(),
      durationAmount: draft.durationAmount,
    };
    if (this.warningEnabled) {
      if (!warningAction || typeof warningLeadMinutes !== 'number')
        throw new Error(
          'Choose the Warning operation and lead, or set Warning to Off.',
        );
      candidate.warningAction = structuredClone(warningAction);
      candidate.warningLeadMinutes = warningLeadMinutes;
    } else {
      if (
        warningAction == null &&
        Object.prototype.hasOwnProperty.call(draft, 'warningAction')
      )
        candidate.warningAction = warningAction;
      if (
        warningLeadMinutes == null &&
        Object.prototype.hasOwnProperty.call(draft, 'warningLeadMinutes')
      )
        candidate.warningLeadMinutes = warningLeadMinutes;
    }
    if (this.overdueEnabled) {
      if (!breachAction)
        throw new Error('Choose an Overdue operation, or choose None.');
      candidate.breachAction = structuredClone(breachAction);
    } else if (
      breachAction == null &&
      Object.prototype.hasOwnProperty.call(draft, 'breachAction')
    )
      candidate.breachAction = breachAction;
    return candidate;
  }
  private candidateDocument(
    document: WorkflowDefinitionDocument,
  ): WorkflowDefinitionDocument {
    if (!this.nodeId || !this.catalog)
      throw new Error(
        'Reload the exact installed timer operations before applying.',
      );
    return applyStepTimerChange(
      document,
      {
        nodeId: this.nodeId,
        timerIndex:
          this.removalId !== null ? this.removalIndex : this.originalIndex,
        timerId: this.removalId !== null ? this.removalId : this.originalId,
        originalTimers: this.originalTimers,
      },
      this.removalId !== null
        ? { kind: 'remove' }
        : {
            kind: 'upsert',
            timer:
              this.draft?.version === 2
                ? this.optionalCandidate()
                : this.legacyCandidate(),
          },
      this.draft?.version === 2 && this.removalId === null
        ? {
            catalog: this.catalogLease?.snapshot.catalog ?? null,
            calendars: this.catalogCalendars,
            lease: this.catalogLease,
            currentRequest: this.requestFor(document),
          }
        : { catalog: this.catalog, calendars: this.calendars },
    );
  }
  isCurrentOwnedDocumentChange(
    event: WorkflowTimerOwnedDocumentChange,
    current: WorkflowDefinitionDocument,
  ): boolean {
    if (
      !this.workspaceMode ||
      this.emitting !== event ||
      !this.dirty ||
      !this.bufferCurrent(current) ||
      this.disabled ||
      !this.canAuthor ||
      event.nodeId !== this.nodeId ||
      event.owner !== this.ownerKey() ||
      event.view !== (this.activeView?.() ?? null) ||
      event.source !== JSON.stringify(current) ||
      JSON.stringify(event.draft) !== JSON.stringify(this.draftSnapshot)
    )
      return false;
    try {
      return (
        JSON.stringify(event.document) ===
        JSON.stringify(this.candidateDocument(current))
      );
    } catch {
      return false;
    }
  }
  private acceptCandidate(
    current: WorkflowDefinitionDocument,
    next: WorkflowDefinitionDocument,
  ): boolean {
    if (!this.workspaceMode) {
      this.document = next;
      this.resetPendingChanges();
      this.documentChange.emit(next);
      return true;
    }
    const owner = this.ownerKey(),
      namespace = this.namespace(),
      draft = JSON.stringify(this.draftSnapshot),
      expected = JSON.stringify(next);
    const event: WorkflowTimerOwnedDocumentChange = {
      document: next,
      nodeId: this.nodeId!,
      owner,
      source: JSON.stringify(current),
      view: this.activeView?.() ?? null,
      draft: this.draftSnapshot,
    };
    this.emitting = event;
    try {
      this.ownedDocumentChange.emit(event);
    } finally {
      this.emitting = null;
    }
    const receipt = this.currentDocument();
    if (
      !receipt ||
      receipt === current ||
      owner !== this.ownerKey() ||
      namespace !== this.namespace() ||
      draft !== JSON.stringify(this.draftSnapshot) ||
      expected !== JSON.stringify(receipt)
    ) {
      this.error =
        'Timer input was not applied to the current draft. Your input is retained.';
      return false;
    }
    this.document = receipt;
    this.resetPendingChanges();
    return true;
  }
  applyPendingChanges(document = this.currentDocument()): boolean {
    if (this.emitting) return false;
    if (!this.dirty) return true;
    if (
      document &&
      this.draft?.version === 2 &&
      this.optionalStageAuthoring &&
      this.workspaceMode &&
      !this.leaseCurrent(document) &&
      this.bufferCurrent(document) &&
      this.canAuthor &&
      !this.disabled &&
      !this.interactionBlocked
    ) {
      const currentRequest = this.requestFor(document);
      if (
        !this.loading ||
        JSON.stringify(this.catalogRequest) !== JSON.stringify(currentRequest)
      )
        void this.loadCatalog();
      this.error =
        'Refreshing timer availability for the current draft. Your input is kept; retry Apply or Save after it finishes.';
      return false;
    }
    if (
      !document ||
      this.editingDisabled ||
      !this.draft ||
      !this.nodeId ||
      this.ambiguous
    )
      return false;
    if (
      !this.ownerCurrent() ||
      !this.pinsCurrent(document) ||
      (this.workspaceMode &&
        JSON.stringify(document) !== JSON.stringify(this.currentDocument())) ||
      this.documentReference(document) !== this.draftReference ||
      this.scopeFingerprint(document) !== this.original
    ) {
      this.error =
        'The step or installed pins changed while this timer was open. Your inputs are kept; discard and reopen the current configuration.';
      return false;
    }
    try {
      return this.acceptCandidate(document, this.candidateDocument(document));
    } catch (error) {
      this.error =
        error instanceof Error
          ? error.message
          : 'The timer could not be applied.';
      return false;
    }
  }
  apply(origin?: TimerOrigin): void {
    if (!this.nativeCurrent(origin)) return;
    if (!this.applyPendingChanges())
      this.focusPendingChanges(this.workspaceMode);
  }
  remove(timer: unknown, origin?: TimerOrigin): void {
    if (
      !this.nativeCurrent(origin) ||
      !this.canInspectEdit(timer) ||
      !this.timers.includes(timer) ||
      this.editingDisabled ||
      this.ambiguous ||
      !this.applyPendingChanges() ||
      !this.document ||
      !this.nodeId
    )
      return;
    try {
      const current = this.currentDocument()!;
      const index = this.timers.findIndex(
        (item) => this.timerId(item) === timer.id,
      );
      const next = applyStepTimerChange(
        current,
        {
          nodeId: this.nodeId,
          timerIndex: index,
          timerId: timer.id,
          originalTimers: this.originalTimers,
        },
        { kind: 'remove' },
        { catalog: this.catalog, calendars: this.calendars },
      );
      if (!this.workspaceMode) {
        this.document = next;
        this.resetPendingChanges();
        this.documentChange.emit(next);
      } else {
        this.removalId = timer.id;
        this.removalIndex = index;
        this.original = this.scopeFingerprint(current);
        this.draftReference = this.documentReference(current);
        this.dirty = true;
        try {
          if (!this.acceptCandidate(current, next)) this.dirty = false;
        } finally {
          this.removalId = null;
          this.removalIndex = null;
        }
      }
    } catch (error) {
      this.error =
        error instanceof Error
          ? error.message
          : 'The timer could not be removed.';
    }
  }
  resetPendingChanges(): void {
    this.loadSavedTimers();
    this.draft = null;
    this.warningEnabled = true;
    this.overdueEnabled = true;
    this.originalId = null;
    this.originalIndex = null;
    this.removalIndex = null;
    this.original =
      this.workspaceMode && this.document
        ? this.scopeFingerprint(this.document)
        : '';
    this.draftReference = this.workspaceMode
      ? this.documentReference(this.document)
      : '';
    this.savedPins = Array.isArray(this.document?.capabilityReferences)
      ? structuredClone(this.document.capabilityReferences)
      : [];
    this.ownerSnapshot = this.ownerKey();
    this.renderOwnerSnapshot = this.ownerKey();
    this.namespaceSnapshot = this.namespace(this.document);
    this.formGeneration++;
    this.focusRequest++;
    this.dirty = false;
    this.error = '';
    this.pendingChangesChange.emit(false);
  }
  discardPendingChanges(): void {
    if (this.workspaceMode) {
      this.document = this.currentDocument();
      this.context = this.documentReference(this.document);
    }
    this.resetPendingChanges();
  }
  discardVisibleChanges(origin?: TimerOrigin): void {
    if (this.renderCurrent(origin)) this.discardPendingChanges();
  }
  capturePendingTimerInputs(): readonly {
    name: string;
    label: string;
    value: string;
  }[] {
    return !this.dirty && !this.draft
      ? []
      : [
          {
            name: `step-timer-input:${encodeURIComponent(this.nodeId ?? 'unselected')}`,
            label: `Unapplied step timer input for ${this.nodeId ?? 'unselected'}`,
            value: JSON.stringify({
              format: 'phms.workflow-step-timer-input',
              version: 1,
              nodeId: this.nodeId,
              draft: this.draftSnapshot,
              rawSavedTimers: this.savedConfiguration,
              originalOwner: this.ownerSnapshot,
              originalNamespace: this.namespaceSnapshot,
              originalContext: this.context,
              originalPins: this.savedPins,
              catalog: this.catalog,
              catalogLeaseForRecoveryOnly: this.catalogLease,
              runtime: this.runtime,
              calendars: this.calendars,
              loadError: this.loadError,
              calendarError: this.calendarError,
              error: this.error,
            }),
          },
        ];
  }
  @HostListener('focusin') rememberFocus(): void {
    this.focusRequest++;
  }
  focusPendingChanges(reveal = false, path?: string): void {
    const request = ++this.focusRequest,
      owner = this.ownerKey(),
      source = JSON.stringify(this.currentDocument()),
      view = this.activeView?.() ?? null,
      generation = this.formGeneration,
      host = this.element.nativeElement,
      before = host.ownerDocument.activeElement;
    setTimeout(() => {
      const active = host.ownerDocument.activeElement;
      if (
        this.destroyed ||
        request !== this.focusRequest ||
        this.ownerViews?.focusSuspended() ||
        host.closest('[hidden],[inert],[aria-hidden="true"]') ||
        (this.workspaceMode &&
          (owner !== this.ownerKey() ||
            source !== JSON.stringify(this.currentDocument()) ||
            view !== (this.activeView?.() ?? null) ||
            generation !== this.formGeneration ||
            !this.renderPresentationCurrent()))
      )
        return;
      if (
        this.workspaceMode &&
        active !== before &&
        active !== host.ownerDocument.body &&
        active?.isConnected &&
        !active.closest('[hidden],[inert]')
      )
        return;
      const field = path?.match(
        /\.(label|startCode|durationUnit|durationAmount|warningLeadMinutes|pauseCode|resetCode|reentryCode|dueLocalTime|gapCode|overlapCode)$/,
      )?.[1];
      const exact = field
        ? host.querySelector<HTMLElement>(
            `[data-timer-field="${field}"]:not(:disabled)`,
          )
        : null;
      const target =
        exact ??
        host.querySelector<HTMLElement>('[role="alert"][tabindex]') ??
        host.querySelector<HTMLElement>(
          '.timer-form input:not(:disabled),.timer-form select:not(:disabled),[data-timer-heading]',
        );
      if (!target?.isConnected) return;
      const expected =
        request + (host.ownerDocument.activeElement === target ? 0 : 1);
      target.focus({ preventScroll: this.workspaceMode });
      if (
        reveal &&
        this.workspaceMode &&
        host.ownerDocument.activeElement === target &&
        this.focusRequest === expected &&
        target.isConnected &&
        owner === this.ownerKey() &&
        source === JSON.stringify(this.currentDocument()) &&
        view === (this.activeView?.() ?? null) &&
        generation === this.formGeneration &&
        this.renderPresentationCurrent() &&
        !this.ownerViews?.focusSuspended()
      )
        target.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    });
  }
}
